<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { parseDateToken } from '../../tasks/composables/useSmartTextParser';

const props = defineProps({
  date: { type: String, default: null },
  time: { type: String, default: null },
});

const emit = defineEmits(['update:date', 'update:time', 'close']);

const { t } = useI18n();

const today = new Date();

// --- digitação livre ---
const freeInput = ref('');
const freeInputEl = ref(null);
const freeInputError = ref(false);

function formatDateDisplay2(dateStr, timeStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const dd = String(d).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  const yy = String(y).slice(2);
  return timeStr ? `${dd}/${mm}/${yy} ${timeStr}` : `${dd}/${mm}/${yy}`;
}

watch(
  [() => props.date, () => props.time],
  () => {
    freeInput.value = formatDateDisplay2(props.date, props.time);
    freeInputError.value = false;
  },
  { immediate: true }
);

function commitFreeInput() {
  const raw = freeInput.value.trim();
  if (!raw) return;
  const result = parseDateToken(raw);
  if (!result) {
    freeInputError.value = true;
    return;
  }
  freeInputError.value = false;
  selectedDate.value = result.due_date;
  const d = new Date(result.due_date + 'T12:00:00');
  viewYear.value = d.getFullYear();
  viewMonth.value = d.getMonth();
  emit('update:date', result.due_date);
  if (result.due_time) {
    timeRaw.value = result.due_time.replace(':', '');
    showTimeInput.value = true;
    emit('update:time', result.due_time);
  }
}

function onFreeInputKeydown(e) {
  if (e.key === 'Enter') {
    e.preventDefault();
    commitFreeInput();
  }
  if (e.key === 'Escape') {
    freeInput.value = formatDateDisplay2(props.date, props.time);
    freeInputError.value = false;
  }
}
const tomorrow = new Date(today);
tomorrow.setDate(today.getDate() + 1);

const viewYear = ref(today.getFullYear());
const viewMonth = ref(today.getMonth());
const selectedDate = ref(props.date ?? null);

const showTimeInput = ref(false);
const timeRaw = ref(props.time ? props.time.replace(':', '') : '');
const timeInputEl = ref(null);

watch(
  () => props.date,
  v => {
    selectedDate.value = v ?? null;
  }
);
watch(
  () => props.time,
  v => {
    if (!showTimeInput.value) timeRaw.value = v ? v.replace(':', '') : '';
  }
);

function parseTime(raw) {
  if (!raw || raw.length === 0) return null;
  const digits = raw.padEnd(4, '0');
  const h = parseInt(digits.slice(0, 2), 10);
  const m = parseInt(digits.slice(2, 4), 10);
  if (h > 23 || m > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatTimeDisplay(raw) {
  if (!raw) return '';
  if (raw.length <= 2) return raw;
  return `${raw.slice(0, 2)}:${raw.slice(2)}`;
}

function syncInputDisplay() {
  if (!timeInputEl.value) return;
  timeInputEl.value.value = formatTimeDisplay(timeRaw.value);
  const len = timeInputEl.value.value.length;
  timeInputEl.value.setSelectionRange(len, len);
}

watch(showTimeInput, val => {
  if (val) nextTick(() => syncInputDisplay());
});

const builtTime = computed(() => parseTime(timeRaw.value));

const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

const monthLabel = computed(() =>
  new Date(viewYear.value, viewMonth.value, 1)
    .toLocaleDateString('pt-BR', { month: 'long' })
    .replace(/^\w/, c => c.toUpperCase())
);

const calendarDays = computed(() => {
  const firstDay = new Date(viewYear.value, viewMonth.value, 1);
  const lastDay = new Date(viewYear.value, viewMonth.value + 1, 0);
  const days = [];
  for (let i = 0; i < firstDay.getDay(); i += 1) {
    const d = new Date(
      viewYear.value,
      viewMonth.value,
      -firstDay.getDay() + 1 + i
    );
    days.push({ date: d, currentMonth: false });
  }
  for (let i = 1; i <= lastDay.getDate(); i += 1) {
    days.push({
      date: new Date(viewYear.value, viewMonth.value, i),
      currentMonth: true,
    });
  }
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i += 1) {
    days.push({
      date: new Date(viewYear.value, viewMonth.value + 1, i),
      currentMonth: false,
    });
  }
  return days;
});

function isToday(date) {
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

function isSelected(date) {
  if (!selectedDate.value) return false;
  const [y, m, d] = selectedDate.value.split('-').map(Number);
  return (
    date.getFullYear() === y &&
    date.getMonth() === m - 1 &&
    date.getDate() === d
  );
}

function toLocalISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function selectDay(date) {
  selectedDate.value = toLocalISODate(date);
  emit('update:date', selectedDate.value);
}

function prevMonth() {
  if (viewMonth.value === 0) {
    viewMonth.value = 11;
    viewYear.value -= 1;
  } else viewMonth.value -= 1;
}

function nextMonth() {
  if (viewMonth.value === 11) {
    viewMonth.value = 0;
    viewYear.value += 1;
  } else viewMonth.value += 1;
}

function prevYear() {
  viewYear.value -= 1;
}

function nextYear() {
  viewYear.value += 1;
}

function setToday() {
  selectedDate.value = toLocalISODate(today);
  viewYear.value = today.getFullYear();
  viewMonth.value = today.getMonth();
  emit('update:date', selectedDate.value);
}

function setTomorrow() {
  selectedDate.value = toLocalISODate(tomorrow);
  viewYear.value = tomorrow.getFullYear();
  viewMonth.value = tomorrow.getMonth();
  emit('update:date', selectedDate.value);
}

function clearDate() {
  selectedDate.value = null;
  timeRaw.value = '';
  showTimeInput.value = false;
  emit('update:date', null);
  emit('update:time', null);
  emit('close');
}

function clearTime() {
  timeRaw.value = '';
  showTimeInput.value = false;
  emit('update:time', null);
}

function commitTime() {
  if (timeRaw.value.length >= 1 && builtTime.value)
    emit('update:time', builtTime.value);
}

function onTimeKeydown(e) {
  if (e.key === 'Enter') {
    e.preventDefault();
    commitTime();
    showTimeInput.value = false;
    return;
  }
  if (e.key === 'Escape') {
    e.preventDefault();
    timeRaw.value = props.time ? props.time.replace(':', '') : '';
    syncInputDisplay();
    showTimeInput.value = false;
    return;
  }
  if (e.key === 'Backspace') {
    e.preventDefault();
    const el = timeInputEl.value;
    const pos = el.selectionStart;
    const display = formatTimeDisplay(timeRaw.value);
    const selEnd = el.selectionEnd;
    if (pos !== selEnd) {
      let newRaw = '';
      let di = 0;
      for (let i = 0; i < display.length; i += 1) {
        if (display[i] !== ':') {
          if (i < pos || i >= selEnd) newRaw += timeRaw.value[di];
          di += 1;
        }
      }
      timeRaw.value = newRaw;
    } else if (pos > 0) {
      let rawIdx = 0;
      for (let i = 0; i < pos; i += 1) {
        if (display[i] !== ':') rawIdx += 1;
      }
      rawIdx -= 1;
      if (rawIdx >= 0)
        timeRaw.value =
          timeRaw.value.slice(0, rawIdx) + timeRaw.value.slice(rawIdx + 1);
    }
    const newDisplay = formatTimeDisplay(timeRaw.value);
    el.value = newDisplay;
    let newPos = pos > 0 ? pos - 1 : 0;
    if (newPos > 0 && newDisplay[newPos - 1] === ':') newPos -= 1;
    el.setSelectionRange(newPos, newPos);
    return;
  }
  if (/^\d$/.test(e.key)) {
    e.preventDefault();
    if (timeRaw.value.length >= 4) return;
    const el = timeInputEl.value;
    const pos = el.selectionStart;
    const display = formatTimeDisplay(timeRaw.value);
    let rawIdx = 0;
    for (let i = 0; i < pos; i += 1) {
      if (display[i] !== ':') rawIdx += 1;
    }
    timeRaw.value =
      timeRaw.value.slice(0, rawIdx) + e.key + timeRaw.value.slice(rawIdx);
    const newDisplay = formatTimeDisplay(timeRaw.value);
    el.value = newDisplay;
    let newPos = pos + 1;
    if (newDisplay[newPos] === ':') newPos += 1;
    el.setSelectionRange(newPos, newPos);
  }
}
</script>

<template>
  <div class="relative" tabindex="-1" @click.stop>
    <div
      class="bg-n-background border border-n-weak rounded-xl shadow-xl w-72 overflow-hidden"
    >
      <!-- Input de digitação livre -->
      <div class="px-3 pt-2.5 pb-2 border-b border-n-weak">
        <input
          ref="freeInputEl"
          v-model="freeInput"
          type="text"
          placeholder="dd/mm/aaaa hh:mm"
          class="w-full text-sm bg-n-surface-2 text-n-slate-12 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 placeholder:text-n-slate-9"
          :class="
            freeInputError
              ? 'ring-1 ring-n-ruby-9 text-n-ruby-11'
              : 'ring-n-brand'
          "
          @keydown="onFreeInputKeydown"
          @blur="commitFreeInput"
        />
      </div>

      <!-- Atalhos -->
      <div class="flex gap-1.5 px-3 py-2 border-b border-n-weak">
        <button
          type="button"
          class="flex-1 text-sm py-1 rounded-lg border border-n-weak hover:bg-n-alpha-1 transition-colors font-medium whitespace-nowrap"
          :class="
            selectedDate === toLocalISODate(today)
              ? 'text-n-brand border-n-brand'
              : 'text-n-slate-12'
          "
          @click="setToday"
        >
          {{ t('TASKS.DATE_SHORTCUTS.TODAY') }}
        </button>
        <button
          type="button"
          class="flex-1 text-sm py-1 rounded-lg border border-n-weak hover:bg-n-alpha-1 transition-colors whitespace-nowrap"
          :class="
            selectedDate === toLocalISODate(tomorrow)
              ? 'text-n-brand border-n-brand'
              : 'text-n-slate-12'
          "
          @click="setTomorrow"
        >
          {{ t('TASKS.DATE_SHORTCUTS.TOMORROW') }}
        </button>
        <button
          type="button"
          class="flex-1 text-sm py-1 rounded-lg border border-n-weak hover:bg-n-alpha-1 transition-colors whitespace-nowrap"
          :class="
            selectedDate === null
              ? 'text-n-brand border-n-brand'
              : 'text-n-slate-12'
          "
          @click="clearDate"
        >
          {{ t('TASKS.DATE_SHORTCUTS.NO_DATE') }}
        </button>
      </div>

      <!-- Cabeçalho do mês -->
      <div class="flex items-center justify-between px-4 py-2">
        <div class="flex items-center">
          <button
            type="button"
            class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-11"
            @click="prevYear"
          >
            <span class="i-lucide-chevrons-left size-4" />
          </button>
          <button
            type="button"
            class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-11"
            @click="prevMonth"
          >
            <span class="i-lucide-chevron-left size-4" />
          </button>
        </div>
        <span class="text-sm font-medium text-n-slate-12">{{ monthLabel }} {{ viewYear }}</span>
        <div class="flex items-center">
          <button
            type="button"
            class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-11"
            @click="nextMonth"
          >
            <span class="i-lucide-chevron-right size-4" />
          </button>
          <button
            type="button"
            class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-11"
            @click="nextYear"
          >
            <span class="i-lucide-chevrons-right size-4" />
          </button>
        </div>
      </div>

      <!-- Grade de dias -->
      <div class="px-3 pb-2">
        <div class="grid grid-cols-7 mb-1">
          <span
            v-for="wd in WEEKDAYS"
            :key="wd"
            class="text-center text-[11px] font-medium text-n-slate-9 py-1"
            >{{ wd }}</span>
        </div>
        <div class="grid grid-cols-7">
          <button
            v-for="(day, i) in calendarDays"
            :key="i"
            type="button"
            class="flex items-center justify-center h-8 text-sm rounded transition-colors"
            :class="[
              isSelected(day.date)
                ? 'bg-n-brand text-white font-medium'
                : isToday(day.date)
                  ? 'border border-n-brand text-n-brand font-medium hover:bg-n-alpha-2'
                  : day.currentMonth
                    ? 'text-n-slate-12 hover:bg-n-alpha-1'
                    : 'text-n-slate-9 hover:bg-n-alpha-1',
            ]"
            @click="selectDay(day.date)"
          >
            {{ day.date.getDate() }}
          </button>
        </div>
      </div>

      <!-- Campo de horário -->
      <div
        class="border-t border-n-weak flex items-center pl-[24px] pr-3 py-1.5"
        @click.stop
      >
        <!-- Ícone fixo sempre presente -->
        <span
          class="i-lucide-clock size-3.5 flex-shrink-0 mr-2"
          :class="timeRaw.length >= 1 ? 'text-n-brand' : 'text-n-slate-9'"
        />
        <!-- Botão "Adicionar horário" ou input — ocupa o restante -->
        <button
          v-if="!showTimeInput && timeRaw.length === 0"
          type="button"
          class="flex-1 text-left text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors"
          @mousedown.prevent.stop="
            showTimeInput = true;
            nextTick(() => timeInputEl?.focus());
          "
        >
          {{ t('TASKS.DATE_SHORTCUTS.ADD_TIME') }}
        </button>
        <div
          v-else
          class="inline-flex items-center gap-2 px-3 py-1 cursor-text bg-n-surface-2 rounded-lg"
          @click="timeInputEl && timeInputEl.focus()"
        >
          <input
            ref="timeInputEl"
            type="text"
            inputmode="numeric"
            maxlength="5"
            size="5"
            :value="formatTimeDisplay(timeRaw)"
            class="reset-base bg-n-surface-2 text-sm font-medium text-n-slate-12 focus:outline-none"
            :style="{
              backgroundImage: 'none',
              border: '0',
              padding: '0',
              margin: '0',
              outline: '0',
              boxShadow: 'none',
              width: '3rem',
              height: '1.25rem',
              lineHeight: '1.25rem',
              display: 'block',
            }"
            @focus="
              showTimeInput = true;
              syncInputDisplay();
            "
            @keydown="onTimeKeydown"
            @blur="
              commitTime();
              showTimeInput = false;
            "
          />
          <span
            class="i-lucide-x size-3 text-n-ruby-9 hover:text-n-ruby-11 cursor-pointer transition-colors flex-shrink-0"
            @mousedown.stop.prevent="clearTime()"
          />
        </div>
      </div>
    </div>
  </div>
</template>
